/**
 * Step Builder
 *
 * Converts TutorialConfig steps into Shepherd.js step options.
 * Handles variable interpolation, tree node resolution, and action conversion.
 */

import { ShepherdOptionsWithType } from "react-shepherd"
import { NavigateFunction } from "react-router-dom"

import { AppDispatch } from "../../../app/store"
import {
  TutorialConfig,
  TutorialStep,
  StepButton,
  ButtonAction,
} from "../types/tutorial-config"
import { defaults, elementReady } from "../utils"
import { resolveSelector } from "./tree-resolver"
import { createActionExecutor } from "./action-executor"
import { setUserCompletedTutorial } from "../../auth/authSlice"
import { setDurationRelativeToNow } from "../../simulation/simulationThunks"
import { addMessage, setTimerAsCountdown } from "../../simulation/simulationSlice"
import { resetSimulationState, convertToTime } from "../../simulation/utils"
import { Event, Information } from "../../simulation/types"

export type StepBuilderContext = {
  dispatch: AppDispatch
  navigate: NavigateFunction
  testId: string
  runId: string
  selectorMap: Record<string, string>
  /** Simulation Information[] tree - used by treePath actions */
  categories: Information[]
  tourRef: { complete: () => void }
  /** Current seconds elapsed - used for message timestamps */
  getSecondsElapsed: () => number
}

/**
 * Interpolates template variables in text.
 *
 * @param text - Text potentially containing {{variable}} placeholders
 * @param variables - Map of variable names to values
 * @returns Text with variables replaced
 */
export function interpolateVariables(
  text: string,
  variables: Record<string, string>
): string {
  let result = text
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value)
  }
  return result
}

/**
 * Interpolates variables in an array of text strings.
 */
function interpolateTextArray(
  text: string | string[],
  variables: Record<string, string>
): string[] {
  const textArray = Array.isArray(text) ? text : [text]
  return textArray.map((t) => interpolateVariables(t, variables))
}

/**
 * Builds a Shepherd button configuration from a config button definition.
 */
function buildButton(
  button: StepButton,
  context: StepBuilderContext
): { classes: string; text: string; type?: string; action?: () => void } {
  const { dispatch, navigate, testId, runId, tourRef } = context

  const baseButton: {
    classes: string
    text: string
    type?: string
    action?: () => void
  } = {
    classes: button.classes || "shepherd-button-primary",
    text: button.text,
  }

  // Handle built-in button types
  if (button.type && !button.action) {
    baseButton.type = button.type
    return baseButton
  }

  // Handle custom button actions
  const actionMap: Record<ButtonAction, () => void> = {
    next: () => {
      /* handled by type */
    },
    back: () => {
      /* handled by type */
    },
    complete: () => tourRef.complete(),
    practice: () => {
      tourRef.complete()
      dispatch(setDurationRelativeToNow(3 * 60))
      dispatch(setTimerAsCountdown(true))
    },
    goToSimulation: () => {
      tourRef.complete()
      dispatch(setUserCompletedTutorial(true))
      resetSimulationState(dispatch, false)
      navigate(`/simulations/${testId}/${runId}/intro`)
    },
  }

  if (button.action && actionMap[button.action]) {
    baseButton.action = actionMap[button.action]
  }

  return baseButton
}

/**
 * Creates a function that injects messages configured for a specific step.
 * Messages are injected when the step hides (transitions to the next step).
 *
 * @param stepId - The step ID to find messages for
 * @param config - Tutorial config containing messages and variables
 * @param context - Step builder context with dispatch
 * @returns Function to inject messages, or undefined if no messages for this step
 */
function createMessageInjector(
  stepId: string,
  config: TutorialConfig,
  context: StepBuilderContext
): (() => void) | undefined {
  const messages = config.messages.filter((m) => m.injectAfterStep === stepId)

  if (messages.length === 0) {
    return undefined
  }

  return () => {
    const { dispatch, getSecondsElapsed } = context
    const secondsElapsed = getSecondsElapsed()

    // Build the events to add
    const events: Event[] = messages.map((message) => {
      // Interpolate variables in message
      let description = message.description
      let validateResponse = message.validateResponse

      for (const [key, value] of Object.entries(config.variables)) {
        description = description.replace(
          new RegExp(`\\{\\{${key}\\}\\}`, "g"),
          value
        )
        if (validateResponse) {
          validateResponse = validateResponse.replace(
            new RegExp(`\\{\\{${key}\\}\\}`, "g"),
            value
          )
        }
      }

      return {
        id: typeof message.id === "string" ? parseInt(message.id) || Date.now() : message.id,
        description,
        validateResponse,
        dateTime: "1999-12-31T00:00:00Z",
        elapsedTime: convertToTime(secondsElapsed),
        type: message.type,
      } as Event
    })

    // Append each message individually to preserve existing messages
    events.forEach((event) => {
      dispatch(addMessage(event))
    })
  }
}

/**
 * Builds the default buttons array for a step.
 */
function buildDefaultButtons(
  step: TutorialStep
): { classes: string; text: string; type: string }[] | undefined {
  // Steps with advanceOn typically don't need buttons
  if (step.advanceOn && !step.buttons) {
    return []
  }

  // Use step-specific buttons if defined
  if (step.buttons) {
    return undefined // Will be built separately
  }

  // Default "Next" button
  return [
    {
      classes: "shepherd-button-primary",
      text: "Next",
      type: "next",
    },
  ]
}

/**
 * Builds a single Shepherd step from a config step definition.
 */
export function buildStep(
  step: TutorialStep,
  config: TutorialConfig,
  context: StepBuilderContext
): ShepherdOptionsWithType {
  const { selectorMap } = context

  const shepherdStep: ShepherdOptionsWithType = {
    ...defaults,
    id: step.id,
    classes: step.classes || "meseekna-introjs-tooltip",
    text: interpolateTextArray(step.text, config.variables),
    arrow: step.arrow,
  }

  // Handle attachTo
  if (step.attachTo) {
    shepherdStep.attachTo = {
      element: resolveSelector(step.attachTo.element, selectorMap),
      on: step.attachTo.on,
    }
  }

  // Handle advanceOn
  if (step.advanceOn) {
    shepherdStep.advanceOn = {
      selector: resolveSelector(step.advanceOn.selector, selectorMap),
      event: step.advanceOn.event,
    }
  }

  // Handle buttons
  if (step.buttons) {
    shepherdStep.buttons = step.buttons.map((btn) => buildButton(btn, context))
  } else {
    const defaultButtons = buildDefaultButtons(step)
    if (defaultButtons) {
      shepherdStep.buttons = defaultButtons
    }
  }

  // Handle modal overlay settings
  if (step.modalOverlayOpeningPadding !== undefined) {
    shepherdStep.modalOverlayOpeningPadding = step.modalOverlayOpeningPadding
  }
  if (step.modalOverlayOpeningRadius !== undefined) {
    shepherdStep.modalOverlayOpeningRadius = step.modalOverlayOpeningRadius
  }

  // Handle waitForElement (beforeShowPromise).
  //
  // A step's advanceOn selector is an implicit prerequisite: Shepherd binds the
  // advance listener synchronously on show and throws ("No element was found for
  // the selector supplied to advanceOn") if the target isn't in the DOM yet. When
  // the target is rendered by React off the back of a dispatch in the previous
  // step's hide handler (e.g. injected messages) it isn't there in time, so fall
  // back to waiting for the advanceOn selector when no explicit wait is given.
  // elementReady resolves on a timeout rather than hanging, so this can only
  // delay a step, never block the tour.
  // The builder writes waitForElement as "" for steps that don't set one, so
  // treat blank as absent rather than as "wait for nothing".
  const waitForElement =
    step.waitForElement?.trim() || step.advanceOn?.selector?.trim()
  if (waitForElement) {
    const selector = resolveSelector(waitForElement, selectorMap)
    shepherdStep.beforeShowPromise = () =>
      elementReady(selector).then(() => undefined)
  }

  // Handle lifecycle actions and message injection
  const showAction = createActionExecutor(step.onShow, selectorMap, context.categories)
  const hideAction = createActionExecutor(step.onHide, selectorMap, context.categories)
  const messageInjector = createMessageInjector(step.id, config, context)

  // Combine hide action with message injection if both exist
  const combinedHideAction = (): void => {
    if (hideAction) {
      hideAction()
    }
    if (messageInjector) {
      messageInjector()
    }
  }

  const hasHideAction = hideAction || messageInjector

  if (showAction || hasHideAction) {
    shepherdStep.when = {}
    if (showAction) {
      shepherdStep.when.show = showAction
    }
    if (hasHideAction) {
      shepherdStep.when.hide = combinedHideAction
    }
  }

  return shepherdStep
}

/**
 * Builds all Shepherd steps from a tutorial configuration.
 *
 * @param config - The tutorial configuration
 * @param context - Context with dispatch, navigate, and tour reference
 * @returns Array of Shepherd step options
 */
export function buildStepsFromConfig(
  config: TutorialConfig,
  context: StepBuilderContext
): ShepherdOptionsWithType[] {
  return config.steps.map((step) => buildStep(step, config, context))
}
